import React, { useState, useEffect } from 'react';

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  membershipStatus: string;
}

interface AlumniMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  yearsActive: string;
}

interface Notification {
  id: string;
  title: string;
  targetAudience: 'all' | 'active_families' | 'overdue_payments' | 'specific_families' | 'all_alumni' | 'specific_alumni' | 'families_and_alumni';
  specificFamilyIds: string | null;
  specificAlumniIds?: string | null;
  targetFamilies?: {
    id: string;
    familyName: string;
    primaryContactFirstName: string;
    primaryContactLastName: string;
  }[];
}

interface ManageNotificationFamiliesModalProps {
  notification: Notification;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ManageNotificationFamiliesModal({ notification, onClose, onSuccess }: ManageNotificationFamiliesModalProps) {
  const [targetAudience, setTargetAudience] = useState(notification.targetAudience);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>(
    notification.specificFamilyIds ? JSON.parse(notification.specificFamilyIds) : []
  );
  const [selectedAlumniIds, setSelectedAlumniIds] = useState<string[]>(
    notification.specificAlumniIds ? JSON.parse(notification.specificAlumniIds) : []
  );
  const [allFamilies, setAllFamilies] = useState<Family[]>([]);
  const [allAlumni, setAllAlumni] = useState<AlumniMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [familySearchTerm, setFamilySearchTerm] = useState('');
  const [alumniSearchTerm, setAlumniSearchTerm] = useState('');

  useEffect(() => {
    fetchFamilies();
    fetchAlumni();
  }, []);

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setAllFamilies(data.data);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const fetchAlumni = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/alumni', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setAllAlumni(data.data);
      }
    } catch (error) {
      console.error('Error fetching alumni:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      
      // Build the request body - only include specific IDs if they're relevant
      const requestBody: any = {
        targetAudience
      };
      
      if (targetAudience === 'specific_families') {
        requestBody.specificFamilyIds = selectedFamilyIds;
      }
      
      if (targetAudience === 'specific_alumni') {
        requestBody.specificAlumniIds = selectedAlumniIds;
      }
      
      const response = await fetch(`/api/notifications/${notification.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        alert('Notification recipients updated successfully!');
        onSuccess();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating notification:', error);
      alert('Failed to update notification');
    } finally {
      setLoading(false);
    }
  };

  const toggleFamily = (familyId: string) => {
    if (selectedFamilyIds.includes(familyId)) {
      setSelectedFamilyIds(selectedFamilyIds.filter(id => id !== familyId));
    } else {
      setSelectedFamilyIds([...selectedFamilyIds, familyId]);
    }
  };

  const toggleAlumni = (alumniId: string) => {
    if (selectedAlumniIds.includes(alumniId)) {
      setSelectedAlumniIds(selectedAlumniIds.filter(id => id !== alumniId));
    } else {
      setSelectedAlumniIds([...selectedAlumniIds, alumniId]);
    }
  };

  const selectAllFilteredFamilies = () => {
    const filtered = filteredFamilies.map(f => f.id);
    setSelectedFamilyIds([...new Set([...selectedFamilyIds, ...filtered])]);
  };

  const deselectAllFilteredFamilies = () => {
    const filtered = filteredFamilies.map(f => f.id);
    setSelectedFamilyIds(selectedFamilyIds.filter(id => !filtered.includes(id)));
  };

  const selectAllFilteredAlumni = () => {
    const filtered = filteredAlumni.map(a => a.id);
    setSelectedAlumniIds([...new Set([...selectedAlumniIds, ...filtered])]);
  };

  const deselectAllFilteredAlumni = () => {
    const filtered = filteredAlumni.map(a => a.id);
    setSelectedAlumniIds(selectedAlumniIds.filter(id => !filtered.includes(id)));
  };

  const filteredFamilies = allFamilies.filter(family =>
    family.familyName.toLowerCase().includes(familySearchTerm.toLowerCase()) ||
    `${family.primaryContactFirstName} ${family.primaryContactLastName}`.toLowerCase().includes(familySearchTerm.toLowerCase())
  );

  const filteredAlumni = allAlumni.filter(alumni =>
    `${alumni.firstName} ${alumni.lastName}`.toLowerCase().includes(alumniSearchTerm.toLowerCase()) ||
    (alumni.email && alumni.email.toLowerCase().includes(alumniSearchTerm.toLowerCase()))
  );

  const getTargetDescription = () => {
    switch (targetAudience) {
      case 'all':
        return `All families and alumni (${allFamilies.length + allAlumni.length} total)`;
      case 'active_families':
        return `Active families (${allFamilies.filter(f => f.membershipStatus === 'active').length})`;
      case 'specific_families':
        return `${selectedFamilyIds.length} selected families`;
      case 'all_alumni':
        return `All alumni (${allAlumni.length})`;
      case 'specific_alumni':
        return `${selectedAlumniIds.length} selected alumni`;
      case 'families_and_alumni':
        return `All families and alumni (${allFamilies.length + allAlumni.length} total)`;
      case 'overdue_payments':
        return 'Families with overdue payments';
      default:
        return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col h-full max-h-[90vh]">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">👥 Manage Recipients</h3>
                <p className="text-sm text-gray-600 mt-1">{notification.title}</p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {/* Target Audience Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
              <select
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value as any)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
              >
                <option value="all">👥 All (Families & Alumni)</option>
                <option value="active_families">👨‍👩‍👧‍👦 Active Families Only</option>
                <option value="all_alumni">🎓 All Alumni</option>
                <option value="overdue_payments">💰 Families with Overdue Payments</option>
                <option value="specific_families">👥 Specific Families</option>
                <option value="specific_alumni">🎓 Specific Alumni</option>
                <option value="families_and_alumni">👥🎓 Families and Alumni</option>
              </select>
              <p className="text-sm text-gray-600 mt-1">Current: {getTargetDescription()}</p>
            </div>

            {/* Family Selection (only for specific_families) */}
            {targetAudience === 'specific_families' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700">Select Families</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllFilteredFamilies}
                      className="text-sm text-ggaa-green hover:text-ggaa-green/80 font-medium"
                    >
                      Select All
                    </button>
                    <span className="text-gray-400">|</span>
                    <button
                      type="button"
                      onClick={deselectAllFilteredFamilies}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search families..."
                    value={familySearchTerm}
                    onChange={(e) => setFamilySearchTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  />
                </div>

                {/* Family List */}
                <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                  {filteredFamilies.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No families found</div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredFamilies.map((family) => (
                        <label
                          key={family.id}
                          className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFamilyIds.includes(family.id)}
                            onChange={() => toggleFamily(family.id)}
                            className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="font-medium text-gray-900">{family.familyName}</div>
                            <div className="text-sm text-gray-600">
                              {family.primaryContactFirstName} {family.primaryContactLastName}
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            family.membershipStatus === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {family.membershipStatus}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Alumni Selection (only for specific_alumni) */}
            {targetAudience === 'specific_alumni' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700">Select Alumni</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllFilteredAlumni}
                      className="text-sm text-ggaa-green hover:text-ggaa-green/80 font-medium"
                    >
                      Select All
                    </button>
                    <span className="text-gray-400">|</span>
                    <button
                      type="button"
                      onClick={deselectAllFilteredAlumni}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search alumni..."
                    value={alumniSearchTerm}
                    onChange={(e) => setAlumniSearchTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  />
                </div>

                {/* Alumni List */}
                <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                  {filteredAlumni.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No alumni found</div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredAlumni.map((alumni) => (
                        <label
                          key={alumni.id}
                          className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAlumniIds.includes(alumni.id)}
                            onChange={() => toggleAlumni(alumni.id)}
                            className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="font-medium text-gray-900">
                              {alumni.firstName} {alumni.lastName}
                            </div>
                            <div className="text-sm text-gray-600">
                              {alumni.email}
                              {alumni.yearsActive && <span className="ml-2">({alumni.yearsActive})</span>}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-white flex justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading || 
                (targetAudience === 'specific_families' && selectedFamilyIds.length === 0) ||
                (targetAudience === 'specific_alumni' && selectedAlumniIds.length === 0)
              }
              className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Recipients'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 